import React from 'react';
import { Alert, Box, Chip, CircularProgress, Stack, Typography } from '@mui/material';
import { CheckOutlined, ErrorOutline, HourglassEmptyOutlined } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import AssistantMarkdown from './AssistantMarkdown';
import NavigateCard from './NavigateCard';

const humanizeTool = (tool) => String(tool || '').replace(/_/g, ' ');

const ActivityChips = ({ activity, done }) => {
  const { t } = useTranslation();
  if (!activity?.length) return null;
  if (done) {
    const failed = activity.filter((a) => a.state === 'error').length;
    return (
      <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 0.5 }}>
        {t('assistant.activity.summary', { count: activity.length })}
        {failed > 0 ? ` · ${t('assistant.activity.failed', { count: failed })}` : ''}
      </Typography>
    );
  }
  return (
    <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', rowGap: 0.5, mb: 0.75 }} aria-live="polite">
      {activity.map((item) => {
        let icon = <CircularProgress size={12} color="inherit" />;
        let color = 'default';
        if (item.state === 'ok') {
          icon = <CheckOutlined fontSize="small" />;
          color = 'success';
        } else if (item.state === 'error') {
          icon = <ErrorOutline fontSize="small" />;
          color = 'error';
        } else if (item.state === 'pending') {
          icon = <HourglassEmptyOutlined fontSize="small" />;
          color = 'warning';
        }
        const categoryLabel = t(`assistant.status.${item.category || 'updating'}`);
        return (
          <Chip
            key={item.id}
            size="small"
            variant="outlined"
            color={color}
            icon={icon}
            label={`${categoryLabel}: ${humanizeTool(item.tool)}`}
            aria-label={`${categoryLabel} ${humanizeTool(item.tool)}`}
          />
        );
      })}
    </Stack>
  );
};

const MessageBubble = ({ message }) => {
  const { t } = useTranslation();
  const isUser = message.role === 'user';
  const isThinking = !isUser && !message.done && !message.content && !message.activity?.length;

  return (
    <Box
      sx={{ display: 'flex', justifyContent: isUser ? 'flex-end' : 'flex-start' }}
      role="listitem"
      aria-label={isUser ? t('assistant.you') : t('assistant.title')}
    >
      <Box
        sx={{
          maxWidth: '92%',
          px: 1.5,
          py: 1,
          borderRadius: 2,
          borderTopRightRadius: isUser ? 4 : 16,
          borderTopLeftRadius: isUser ? 16 : 4,
          bgcolor: isUser ? 'primary.main' : 'grey.100',
          color: isUser ? 'primary.contrastText' : 'text.primary',
          boxShadow: isUser ? 1 : 0,
        }}
      >
        {isUser ? (
          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
            {message.content}
          </Typography>
        ) : (
          <>
            <ActivityChips activity={message.activity} done={message.done} />
            {isThinking && (
              <Stack direction="row" spacing={1} alignItems="center" aria-live="polite">
                <CircularProgress size={14} color="secondary" />
                <Typography variant="body2" color="text.secondary">{t('assistant.thinking')}</Typography>
              </Stack>
            )}
            {message.content && <AssistantMarkdown>{message.content}</AssistantMarkdown>}
            {!message.done && message.content && (
              <Box component="span" aria-hidden="true" sx={{ display: 'inline-block', width: 6, height: 14, ml: 0.25, bgcolor: 'secondary.main', animation: 'assistant-blink 1s steps(2) infinite', '@keyframes assistant-blink': { to: { visibility: 'hidden' } } }} />
            )}
            {message.navigations?.length > 0 && (
              <Stack direction="row" spacing={0.5} sx={{ mt: 1, flexWrap: 'wrap', rowGap: 0.5 }}>
                {message.navigations.map((nav, index) => <NavigateCard key={`${nav.path}-${index}`} navigation={nav} />)}
              </Stack>
            )}
            {message.error && (
              <Alert severity="error" sx={{ mt: 1, py: 0 }}>{message.error}</Alert>
            )}
            {message.done && !message.content && !message.error && message.activity?.length > 0 && (
              <Typography variant="body2" color="text.secondary">{t('assistant.noText')}</Typography>
            )}
          </>
        )}
      </Box>
    </Box>
  );
};

export default MessageBubble;
